// Оружие в руках у других игроков и ботов (и у себя в виде от третьего
// лица): вшитый в солдата автомат прячется, на его место ставится GLB
// оружия из каталога — та же модель, что от первого лица и на полу.
//
// Привязка:
//   - у солдата есть кость weapone_bone с нодой weapon — по ней (анимации
//     держат руки ровно на ней);
//   - у персонажей без ноды weapon, но с костью weapone_bone (Сыщица,
//     Охотник) — по кости и тому же смещению ноды, что у солдата;
//   - у любого другого персонажа на mixamo-скелете — к кости RightHand со
//     смещением, снятым с солдата (рука → оружие в той же позе).

import type { MeshInstance, Renderer } from "../engine/renderer";
import type { LoadedMesh } from "../engine/meshLoader";
import { WeaponId } from "./catalog";
import type { WeaponAssets } from "./assets";
import { TP_SOLDIER } from "./rig";
import {
  GLTF_TO_WORLD, m4, m4Basis, m4Invert, m4Mul, m4Orthonormal, m4Point, type M4,
} from "./mat";

/** Имена нод вшитого оружия солдата (скрываем, рисуем своё). */
const BUILTIN_WEAPON_NODES = ["weapon", "podstvolnik", "curok"];

/** Нода weapon солдата относительно кости weapone_bone (soldier.glb) —
 *  для персонажей, у которых кость есть, а вшитого автомата нет. */
const WEAPON_NODE_LOCAL: M4 = (() => {
  const [x, y, z, w] = [0.505855917930603, -0.5689510703086853, -0.39712199568748474, 0.5125412344932556];
  const s = 52.16275;
  const m = m4();
  m[0] = (1 - 2 * (y * y + z * z)) * s; m[1] = 2 * (x * y + z * w) * s; m[2] = 2 * (x * z - y * w) * s;
  m[4] = 2 * (x * y - z * w) * s; m[5] = (1 - 2 * (x * x + z * z)) * s; m[6] = 2 * (y * z + x * w) * s;
  m[8] = 2 * (x * z + y * w) * s; m[9] = 2 * (y * z - x * w) * s; m[10] = (1 - 2 * (x * x + y * y)) * s;
  m[12] = -0.24421876668930054; m[13] = 2.9091074466705322; m[14] = -3.395141124725342;
  return m;
})();

interface MeshInfo {
  weaponNode: number;     // нода «weapon» (−1 нет)
  boneNode: number;       // кость weapone_bone (−1 нет)
  handNode: number;       // mixamorig:RightHand (−1 нет)
  hidden: Set<number>;
}

interface Attach {
  inst: MeshInstance;
  weapon: number;
}

export class ThirdPersonWeapons {
  private info = new Map<LoadedMesh, MeshInfo>();
  private attached = new Map<MeshInstance, Attach>();
  /** Рука → оружие (снято с солдата) для персонажей без weapone_bone. */
  private handToWeapon: M4 | null = null;
  private K = m4();     // система нашей модели → нода weapon
  private tmpA = m4();
  private tmpB = m4();
  private tmpC = m4();
  private im = m4();

  constructor(private renderer: Renderer, private assets: WeaponAssets) {
    // K: наш +X (ствол) → −X ноды, наш +Z (верх) → upSign·Y ноды,
    // начало — в точке хвата.
    const s = TP_SOLDIER.upSign;
    const g = TP_SOLDIER.grip;
    m4Basis(this.K, [-1, 0, 0], [0, 0, s], [0, s, 0], g);
  }

  private meshInfo(mesh: LoadedMesh): MeshInfo {
    let mi = this.info.get(mesh);
    if (mi) return mi;
    const hidden = new Set<number>();
    let weaponNode = -1;
    let boneNode = -1;
    let handNode = -1;
    mesh.nodes.forEach((n, i) => {
      const nm = (n.name || "").toLowerCase();
      if (BUILTIN_WEAPON_NODES.includes(nm)) hidden.add(i);
      if (nm === "weapon") weaponNode = i;
      if (nm === "weapone_bone") boneNode = i;
      if (nm.endsWith("righthand")) handNode = i;
    });
    mi = { weaponNode, boneNode, handNode, hidden };
    this.info.set(mesh, mi);
    return mi;
  }

  /** Матрица модели инстанса (как в Renderer.renderMeshes). */
  private instMatrix(inst: MeshInstance): M4 {
    const fy = inst.yaw + inst.yawOffset;
    const c = Math.cos(fy), s = Math.sin(fy), sc = inst.scale;
    const im = this.im;
    im[0] = sc * c; im[1] = sc * s; im[2] = 0; im[3] = 0;
    im[4] = 0; im[5] = 0; im[6] = sc; im[7] = 0;
    im[8] = sc * s; im[9] = -sc * c; im[10] = 0; im[11] = 0;
    im[12] = inst.x; im[13] = inst.y; im[14] = inst.z; im[15] = 1;
    return im;
  }

  private nodeWorld(inst: MeshInstance, node: number, out: M4): M4 {
    const nw = inst.nodeWorldMatrices.subarray(node * 16, node * 16 + 16);
    m4Mul(this.tmpC, this.instMatrix(inst), nw);
    return m4Orthonormal(out, this.tmpC);
  }

  /** Выставить оружие актёру. weapon < 0 — без оружия (и без вшитого). */
  sync(actor: MeshInstance, weapon: number, visible: boolean): [number, number, number] | null {
    const mi = this.meshInfo(actor.mesh);
    if (actor.hiddenNodes !== mi.hidden) actor.hiddenNodes = mi.hidden;
    if (actor.innerMesh) {
      const ii = this.meshInfo(actor.innerMesh);
      if (actor.innerHiddenNodes !== ii.hidden) actor.innerHiddenNodes = ii.hidden;
    }
    let a = this.attached.get(actor);
    const mesh = weapon >= 0 && weapon !== WeaponId.Fists ? this.assets.weaponMesh(weapon) : null;
    if (!mesh || !visible || actor.hidden) {
      if (a) a.inst.hidden = true;
      return null;
    }
    if (!a || a.inst.mesh !== mesh) {
      if (a) this.renderer.removeMeshInstance(a.inst);
      const inst = this.renderer.createMeshInstance({ mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle" });
      inst.worldMatrix = m4();
      this.renderer.addMeshInstance(inst);
      a = { inst, weapon };
      this.attached.set(actor, a);
    }
    a.weapon = weapon;
    a.inst.hidden = false;

    // Система оружия в мире.
    const frame = this.tmpA;
    if (mi.weaponNode >= 0) {
      this.nodeWorld(actor, mi.weaponNode, this.tmpB);
      m4Mul(frame, this.tmpB, this.K);
      if (!this.handToWeapon && mi.handNode >= 0) {
        // Запомнить «рука → оружие» для персонажей без weapone_bone.
        const hand = this.nodeWorld(actor, mi.handNode, m4());
        const inv = m4Invert(m4(), hand);
        this.handToWeapon = m4Mul(m4(), inv, frame);
      }
    } else if (mi.boneNode >= 0) {
      // Кость есть, вшитого автомата нет: та же нода, что у солдата.
      const nw = actor.nodeWorldMatrices.subarray(mi.boneNode * 16, mi.boneNode * 16 + 16);
      m4Mul(this.tmpC, this.instMatrix(actor), nw);
      m4Mul(this.tmpC, this.tmpC, WEAPON_NODE_LOCAL);
      m4Orthonormal(this.tmpB, this.tmpC);
      m4Mul(frame, this.tmpB, this.K);
    } else if (mi.handNode >= 0 && this.handToWeapon) {
      this.nodeWorld(actor, mi.handNode, this.tmpB);
      m4Mul(frame, this.tmpB, this.handToWeapon);
    } else {
      a.inst.hidden = true;
      return null;
    }
    m4Mul(a.inst.worldMatrix!, frame, GLTF_TO_WORLD);
    a.inst.worldAabbDirty = true;
    const pts = this.assets.pts(weapon);
    const mz = pts ? pts.muzzle : [0.6, 0, 0.1];
    return m4Point(frame, mz[0]!, mz[1]!, mz[2]!);
  }

  /** Актёр исчез — убрать его оружие. */
  drop(actor: MeshInstance): void {
    const a = this.attached.get(actor);
    if (a) {
      this.renderer.removeMeshInstance(a.inst);
      this.attached.delete(actor);
    }
  }

  /** Убрать оружие актёров, которых больше нет в списке живых. */
  prune(alive: Set<MeshInstance>): void {
    for (const actor of [...this.attached.keys()]) {
      if (!alive.has(actor)) this.drop(actor);
    }
  }

  clear(): void {
    for (const a of this.attached.values()) this.renderer.removeMeshInstance(a.inst);
    this.attached.clear();
  }
}
